import { lookupConnectorCompat } from "./signalCompatibility";

const XLR_FAMILY = ["xlr", "xlr_combo", "mini_xlr_3pin", "mini_xlr_4pin"];
const BALANCED_CONNECTORS = ["quarter_inch_trs", "eighth_inch_trs", "eighth_inch_trrs"];
const RJ45_FAMILY = ["ethernet", "ultranet", "ethercon"];

// TS plugs fit in TRS jacks (loses balanced/stereo). Use TS cable for these connections.
const TS_DOWNGRADE = {
  quarter_inch_trs: "quarter_inch_ts",
  eighth_inch_trs: "eighth_inch_trs", // no 1/8" TS in the system, keep as-is
};

export function normalizeCombo(connector, otherConnector) {
  if (connector !== "xlr_combo") return connector;
  return otherConnector?.startsWith("quarter_inch") ? otherConnector : "xlr";
}

export function determineSignalCableType(fromPort, toPort) {
  const signalType = fromPort.signal_type;
  const normalizedFrom = normalizeCombo(fromPort.connector, toPort.connector);
  const normalizedTo = normalizeCombo(toPort.connector, fromPort.connector);

  switch (signalType) {
    case "midi":
      return "midi_cable";
    case "usb_audio":
      return "usb_cable";
    case "control":
      return "control_cable";
    case "audio":
      return determineAudioCableType(fromPort, toPort, normalizedFrom, normalizedTo);
    case "adat":
    case "spdif":
    case "aes":
      return "standard";
    default:
      return null;
  }
}

function determineAudioCableType(fromPort, toPort, normalizedFrom, normalizedTo) {
  if (fromPort.signal_level === "speaker" || toPort.signal_level === "speaker") {
    return "speaker";
  }
  // RJ45-family ports (Ethernet, Ultranet, EtherCon) carry digital audio over
  // standard Cat5/Cat6 cables, regardless of the device's signal_type tag.
  if (RJ45_FAMILY.includes(normalizedFrom) || RJ45_FAMILY.includes(normalizedTo)) {
    return "standard";
  }
  if (XLR_FAMILY.includes(normalizedFrom) || XLR_FAMILY.includes(normalizedTo)) {
    return "microphone";
  }
  if (BALANCED_CONNECTORS.includes(normalizedFrom) && BALANCED_CONNECTORS.includes(normalizedTo)) {
    return "balanced_patch";
  }
  return "instrument";
}

function connectorMatch(cableConnector, portConnector) {
  if (!portConnector || !cableConnector) return true;
  return lookupConnectorCompat(cableConnector, portConnector).level === "ok";
}

export function findBestSignalCable(cables, { fromPort, toPort }) {
  const cableType = determineSignalCableType(fromPort, toPort);
  if (!cableType) return null;

  const normalizedInput = normalizeCombo(fromPort.connector, toPort.connector);
  const normalizedOutput = normalizeCombo(toPort.connector, fromPort.connector);

  // Prefer strict equality on both ends.
  const exact = cables.find(
    (c) =>
      c.cable_type === cableType &&
      c.input_connector === normalizedInput &&
      c.output_connector === normalizedOutput,
  );
  if (exact) return exact;

  // Fall back to lookupConnectorCompat-OK pairings (e.g. ethernet plug into
  // ethercon jack, Cat6 cable into ultranet port).
  const compatible = cables.find(
    (c) =>
      c.cable_type === cableType &&
      connectorMatch(c.input_connector, normalizedInput) &&
      connectorMatch(c.output_connector, normalizedOutput),
  );
  if (compatible) return compatible;

  // If normalization didn't change anything, try TS downgrade fallback
  if (normalizedInput === fromPort.connector && normalizedOutput === toPort.connector) {
    // TRS port accepts TS plug — try matching with the simpler connector
    const tsInput = TS_DOWNGRADE[normalizedInput] || normalizedInput;
    const tsOutput = TS_DOWNGRADE[normalizedOutput] || normalizedOutput;
    if (tsInput !== normalizedInput || tsOutput !== normalizedOutput) {
      return cables.find(
        (c) =>
          c.cable_type === cableType &&
          c.input_connector === tsInput &&
          c.output_connector === tsOutput,
      ) || null;
    }
    return null;
  }

  // Try original (unnormalized) connectors
  return (
    cables.find(
      (c) =>
        c.cable_type === cableType &&
        c.input_connector === fromPort.connector &&
        c.output_connector === toPort.connector,
    ) || null
  );
}

export function categorizeSignalCables(groups, fromPort, toPort) {
  const cableType = determineSignalCableType(fromPort, toPort);
  const normalizedInput = normalizeCombo(fromPort.connector, toPort.connector);
  const normalizedOutput = normalizeCombo(toPort.connector, fromPort.connector);

  const best = [];
  const inputPartial = [];
  const outputPartial = [];
  const other = [];

  for (const group of groups) {
    const c = group.cable;
    const typeMatch = c.cable_type === cableType;
    const inputMatch = connectorMatch(c.input_connector, normalizedInput);
    const outputMatch = connectorMatch(c.output_connector, normalizedOutput);

    const isChainOnly = c.cable_type === "coupler" || c.cable_type === "phase_reverser";
    if (typeMatch && inputMatch && outputMatch && !isChainOnly) {
      best.push(group);
    } else if (inputMatch) {
      inputPartial.push(group);
    } else if (outputMatch) {
      outputPartial.push(group);
    } else {
      other.push(group);
    }
  }

  const partial = [...inputPartial, ...outputPartial];

  return [
    { label: "Best match", items: best, showByDefault: true },
    { label: "Partial match", items: partial, showByDefault: true },
    { label: "Other", items: other, showByDefault: false },
  ].filter((cat) => cat.items.length > 0);
}
